import type { StatusKey } from '@bbpos/ui';
import type { PlatformRole, UserRole } from '@bbpos/types';

export type TenantRole = 'OWNER' | 'MANAGER' | 'CAPTAIN' | 'CASHIER';

export type { PlatformRole, UserRole };

export interface UserLocation {
  id: string;
  name: string;
  code?: string;
  is_active?: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  phone?: string;
  is_active: boolean;
  mfa_enabled?: boolean;
  mfa_required?: boolean;
  mfa_required_at?: string | null;
  mfa_required_by?: string | null;
  last_login_at?: string;
  locations?: UserLocation[];
  created_at: string;
  updated_at: string;
  tenant_id?: string;
}

export interface UserPerformance {
  total_sales: number;
  total_bills: number;
  avg_bill_value: number;
  shifts_worked: number;
  total_returns: number;
  avg_items_per_bill: number;
}

export type AttendanceStatus = 'SCHEDULED' | 'PRESENT' | 'ABSENT' | 'LATE' | 'HALF_DAY';

export interface UserShift {
  id: string;
  user_id: string;
  location_id: string;
  location_name?: string;
  shift_name?: string;
  shift_date: string;
  clock_in?: string | null;
  clock_out?: string | null;
  status: AttendanceStatus;
  total_bills?: number;
  total_sales?: number;
}

export interface UserSession {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  deviceInfo: string;
  ip: string;
  lastActive: string;
  isCurrent?: boolean;
}

export interface UserListResponse {
  data: User[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface CreateUserPayload {
  name: string;
  email: string;
  role: TenantRole;
  phone?: string;
  location_ids?: string[];
}

export interface UpdateUserPayload {
  name?: string;
  role?: TenantRole;
  phone?: string;
  is_active?: boolean;
}

export interface LocationListResponse {
  data: UserLocation[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

/** Maps role names to valid Badge StatusKey variants. */
export const ROLE_BADGE_VARIANT: Record<string, StatusKey> = {
  SUPER_ADMIN: 'danger',
  ADMIN: 'active',
  MODERATOR: 'pending',
  OWNER: 'info',
  MANAGER: 'warning',
  CAPTAIN: 'success',
  CASHIER: 'draft',
};

/** Numeric level per role — used for hierarchy gating. */
export const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CAPTAIN: 7,
  CASHIER: 5,
};
